import { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSpinner, FaUsers, FaTimes, FaEnvelope } from 'react-icons/fa';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { SecurityUser } from '../../types/user';
import UserEmailSettings from '../../components/UserEmailSettings';

interface UserModalState {
  isOpen: boolean;
  user: SecurityUser | null;
  mode: 'create' | 'edit';
  activeTab: 'details' | 'email';
}

interface UserFormData {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  groups: number[];
  password?: string;
  confirm_password?: string;
}

const Users = () => {
        const { token } = useAuth();
        const [users, setUsers] = useState<SecurityUser[]>([]);
        const [loading, setLoading] = useState<boolean>(true);
        const [error, setError] = useState<string | null>(null);
        const [searchTerm, setSearchTerm] = useState<string>('');
        const [currentPage, setCurrentPage] = useState<number>(1);
        const itemsPerPage = 8;
        const [availableGroups, setAvailableGroups] = useState<{ id: number; name: string }[]>([]);
        const [saving, setSaving] = useState<boolean>(false);
        
        const [modal, setModal] = useState<UserModalState>({
                isOpen: false,
                user: null,
                mode: 'create',
                activeTab: 'details'
        });
        
        const [formData, setFormData] = useState<UserFormData>({
                username: '',
                email: '',
                first_name: '',
                last_name: '',
                is_active: true,
                groups: [],
                password: '',
                confirm_password: ''
        });

        useEffect(() => {
                fetchUsers();
                fetchGroups();
        }, [token]);
        
        const fetchGroups = async () => {
                if (!token) return;
                
                try {
                        const response = await apiCall(ENDPOINTS.GROUPS.LIST, {
                                method: 'GET'
                        }, token);
                        
                        if (response.ok) {
                                const data = await response.json();
                                setAvailableGroups(data.results || data || []);
                        }
                } catch (err) {
                        console.error('Error fetching groups:', err);
                }
        };

        const fetchUsers = async () => {
                if (!token) return;
                
                try {
                        setLoading(true);
                        const response = await apiCall(ENDPOINTS.USERS.LIST, {
                                method: 'GET'
                        }, token);
                        
                        if (response.ok) {
                                const data = await response.json();
                                setUsers(data.results || data || []);
                                setError(null);
                        } else {
                                throw new Error('Failed to load users');
                        }
                } catch (err) {
                        setError('Failed to load users');
                        console.error('Error fetching users:', err);
                } finally {
                        setLoading(false);
                }
        };

        // Filter users based on search term
        const filteredUsers = users.filter(user =>
                user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Calculate pagination
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
        const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

        const handlePageChange = (newPage: number) => {
                if (newPage >= 1 && newPage <= totalPages) {
                        setCurrentPage(newPage);
                }
        };

        const formatDate = (dateString: string | null) => {
                if (!dateString) return 'Never';
                const date = new Date(dateString);
                return date.toLocaleString('en-AU', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                });
        };

        const getStatusBadge = (isActive: boolean) => {
                return isActive ? (
                        <span className="badge badge-success">Active</span>
                ) : (
                        <span className="badge badge-error">Inactive</span>
                );
        };

        const renderGroups = (groups: string[] | undefined) => {
                if (!groups || groups.length === 0) {
                        return <span className="badge badge-ghost">No groups</span>;
                }
                return groups.map((group, index) => (
                        <span key={index} className="badge badge-primary mr-1">
                                {group}
                        </span>
                ));
        };
        
        const openUserModal = (user?: SecurityUser) => {
                if (user) {
                        // Editing existing user
                        setFormData({
                                username: user.username || '',
                                email: user.email || '',
                                first_name: user.first_name || '',
                                last_name: user.last_name || '',
                                is_active: user.is_active !== undefined ? user.is_active : true,
                                groups: [], // Will need to be mapped from group names to IDs
                                password: '',
                                confirm_password: ''
                        });
                } else {
                        // Creating new user
                        setFormData({
                                username: '',
                                email: '',
                                first_name: '',
                                last_name: '',
                                is_active: true,
                                groups: [],
                                password: '',
                                confirm_password: ''
                        });
                }
                
                setModal({
                        isOpen: true,
                        user: user || null,
                        mode: user ? 'edit' : 'create',
                        activeTab: 'details'
                });
        };
        
        const closeUserModal = () => {
                setModal({
                        isOpen: false,
                        user: null,
                        mode: 'create',
                        activeTab: 'details'
                });
                setFormData({
                        username: '',
                        email: '',
                        first_name: '',
                        last_name: '',
                        is_active: true,
                        groups: [],
                        password: '',
                        confirm_password: ''
                });
        };
        
        const switchTab = (tab: 'details' | 'email') => {
                setModal(prev => ({ ...prev, activeTab: tab }));
        };
        
        const handleInputChange = (field: keyof UserFormData, value: any) => {
                setFormData(prev => ({ ...prev, [field]: value }));
        };
        
        const handleSaveUser = async () => {
                if (!token) return;
                
                // Validation
                if (!formData.username || !formData.email) {
                        alert('Username and email are required');
                        return;
                }
                
                if (modal.mode === 'create') {
                        if (!formData.password || !formData.confirm_password) {
                                alert('Password is required for new users');
                                return;
                        }
                        if (formData.password !== formData.confirm_password) {
                                alert('Passwords do not match');
                                return;
                        }
                }
                
                try {
                        setSaving(true);
                        
                        const endpoint = modal.mode === 'create' 
                                ? ENDPOINTS.USERS.LIST 
                                : `${ENDPOINTS.USERS.DETAIL}${modal.user?.id}/`;
                        
                        const method = modal.mode === 'create' ? 'POST' : 'PATCH';
                        
                        const payload: any = {
                                username: formData.username,
                                email: formData.email,
                                first_name: formData.first_name,
                                last_name: formData.last_name,
                                is_active: formData.is_active,
                                groups: formData.groups
                        };
                        
                        if (modal.mode === 'create' && formData.password) {
                                payload.password = formData.password;
                        }
                        
                        const response = await apiCall(endpoint, {
                                method,
                                body: JSON.stringify(payload)
                        }, token);
                        
                        if (response.ok) {
                                await fetchUsers();
                                closeUserModal();
                                alert(`User ${modal.mode === 'create' ? 'created' : 'updated'} successfully`);
                        } else {
                                const errorData = await response.json();
                                throw new Error(JSON.stringify(errorData));
                        }
                } catch (err) {
                        console.error('Error saving user:', err);
                        alert(`Failed to ${modal.mode === 'create' ? 'create' : 'update'} user`);
                } finally {
                        setSaving(false);
                }
        };

        return (
                <div className="p-6">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-6">
                                <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
                                        <FaUsers className="text-primary" />
                                        Users
                                </h1>
                                <button onClick={() => openUserModal()} className="btn btn-primary">
                                        <FaPlus className="mr-2" />
                                        Add New User
                                </button>
                        </div>

                        {/* Filter Section */}
                        <div className="bg-base-200 p-4 rounded-lg mb-6">
                                <div className="flex items-center gap-4">
                                        <span className="label-text font-semibold whitespace-nowrap">Search Users</span>
                                        <input
                                                type="text"
                                                placeholder="Search by username, email, or name..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="input input-bordered flex-1 max-w-md bg-base-100"
                                        />
                                </div>
                        </div>

                        {loading && (
                                <div className="flex justify-center items-center py-12">
                                        <FaSpinner className="animate-spin text-4xl text-primary" />
                                </div>
                        )}

                        {error && (
                                <div className="alert alert-error mb-4">
                                        <span>{error}</span>
                                </div>
                        )}

                        {!loading && !error && (
                                <div className="bg-base-100 rounded-lg shadow">
                                        <div className="overflow-x-auto">
                                                <table className="table table-zebra w-full">
                                                        <thead>
                                                                <tr>
                                                                        <th>Username</th>
                                                                        <th>Name</th>
                                                                        <th>Email</th>
                                                                        <th>Status</th>
                                                                        <th>Groups</th>
                                                                        <th>Last Login</th>
                                                                        <th>Actions</th>
                                                                </tr>
                                                        </thead>
                                                        <tbody>
                                                                {currentUsers.length === 0 ? (
                                                                        <tr>
                                                                                <td colSpan={7} className="text-center py-8 text-base-content/60">
                                                                                        No users found
                                                                                </td>
                                                                        </tr>
                                                                ) : (
                                                                        currentUsers.map((user) => (
                                                                                <tr key={user.id}>
                                                                                        <td className="font-medium">{user.username}</td>
                                                                                        <td>{user.first_name || user.last_name ? `${user.first_name} ${user.last_name}`.trim() : '-'}</td>
                                                                                        <td>{user.email || '-'}</td>
                                                                                        <td>{getStatusBadge(user.is_active)}</td>
                                                                                        <td>
                                                                                                <div className="flex flex-wrap gap-1">
                                                                                                        {renderGroups(user.groups)}
                                                                                                </div>
                                                                                        </td>
                                                                                        <td>{formatDate(user.last_login)}</td>
                                                                                        <td>
                                                                                                <div className="flex gap-2">
                                                                                                        <button onClick={() => openUserModal(user)} className="btn btn-ghost btn-xs" title="Edit User">
                                                                                                                <FaEdit />
                                                                                                        </button>
                                                                                                        {user.username !== 'admin' && (
                                                                                                                <button className="btn btn-ghost btn-xs text-error" title="Delete User">
                                                                                                                        <FaTrash />
                                                                                                                </button>
                                                                                                        )}
                                                                                                </div>
                                                                                        </td>
                                                                                </tr>
                                                                        ))
                                                                )}
                                                        </tbody>
                                                </table>
                                        </div>

                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                                <div className="flex justify-center items-center gap-2 mt-6 pb-4 flex-shrink-0">
                                                        <button
                                                                onClick={() => handlePageChange(1)}
                                                                disabled={currentPage === 1}
                                                                className="btn btn-sm"
                                                        >
                                                                First
                                                        </button>
                                                        <button
                                                                onClick={() => handlePageChange(currentPage - 1)}
                                                                disabled={currentPage === 1}
                                                                className="btn btn-sm"
                                                        >
                                                                Previous
                                                        </button>

                                                        <span className="px-4">
                                                                Page {currentPage} of {totalPages}
                                                        </span>

                                                        <button
                                                                onClick={() => handlePageChange(currentPage + 1)}
                                                                disabled={currentPage === totalPages}
                                                                className="btn btn-sm"
                                                        >
                                                                Next
                                                        </button>
                                                        <button
                                                                onClick={() => handlePageChange(totalPages)}
                                                                disabled={currentPage === totalPages}
                                                                className="btn btn-sm"
                                                        >
                                                                Last
                                                        </button>
                                                </div>
                                        )}
                                </div>
                        )}
                        
                        {/* User Modal with Tabs */}
                        {modal.isOpen && (
                                <div className="modal modal-open">
                                        <div className="modal-box max-w-4xl max-h-[90vh] flex flex-col p-0">
                                                {/* Modal Header */}
                                                <div className="sticky top-0 bg-base-100 z-10 px-6 py-4 border-b border-base-300">
                                                        <div className="flex justify-between items-center">
                                                                <h3 className="text-2xl font-bold">
                                                                        {modal.mode === 'edit' ? 'Edit User' : 'Add New User'}
                                                                </h3>
                                                                <button onClick={closeUserModal} className="btn btn-ghost btn-sm btn-circle">
                                                                        <FaTimes className="text-xl" />
                                                                </button>
                                                        </div>
                                                        
                                                        {/* Tabs - Only show if editing */}
                                                        {modal.mode === 'edit' && modal.user && (
                                                                <div className="tabs tabs-boxed mt-4 bg-base-200">
                                                                        <button
                                                                                className={`tab ${modal.activeTab === 'details' ? 'tab-active' : ''}`}
                                                                                onClick={() => switchTab('details')}
                                                                        >
                                                                                <FaUsers className="mr-2" />
                                                                                User Details
                                                                        </button>
                                                                        <button
                                                                                className={`tab ${modal.activeTab === 'email' ? 'tab-active' : ''}`}
                                                                                onClick={() => switchTab('email')}
                                                                        >
                                                                                <FaEnvelope className="mr-2" />
                                                                                Email Settings
                                                                        </button>
                                                                </div>
                                                        )}
                                                </div>
                                                
                                                {/* Modal Body */}
                                                <div className="flex-1 overflow-y-auto px-6 py-4">
                                                        {modal.activeTab === 'details' && (
                                                                <div className="space-y-6">
                                                                        {/* User Details Form */}
                                                                        <div className="grid grid-cols-2 gap-4">
                                                                                {/* Username */}
                                                                                <div className="form-control">
                                                                                        <label className="label">
                                                                                                <span className="label-text font-semibold">Username *</span>
                                                                                        </label>
                                                                                        <input
                                                                                                type="text"
                                                                                                value={formData.username}
                                                                                                onChange={(e) => handleInputChange('username', e.target.value)}
                                                                                                className="input input-bordered"
                                                                                                placeholder="Enter username"
                                                                                                disabled={modal.mode === 'edit'}
                                                                                        />
                                                                                </div>
                                                                                
                                                                                {/* Email */}
                                                                                <div className="form-control">
                                                                                        <label className="label">
                                                                                                <span className="label-text font-semibold">Email *</span>
                                                                                        </label>
                                                                                        <input
                                                                                                type="email"
                                                                                                value={formData.email}
                                                                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                                                                className="input input-bordered"
                                                                                                placeholder="Enter email address"
                                                                                        />
                                                                                </div>
                                                                                
                                                                                {/* First Name */}
                                                                                <div className="form-control">
                                                                                        <label className="label">
                                                                                                <span className="label-text font-semibold">First Name</span>
                                                                                        </label>
                                                                                        <input
                                                                                                type="text"
                                                                                                value={formData.first_name}
                                                                                                onChange={(e) => handleInputChange('first_name', e.target.value)}
                                                                                                className="input input-bordered"
                                                                                                placeholder="Enter first name"
                                                                                        />
                                                                                </div>
                                                                                
                                                                                {/* Last Name */}
                                                                                <div className="form-control">
                                                                                        <label className="label">
                                                                                                <span className="label-text font-semibold">Last Name</span>
                                                                                        </label>
                                                                                        <input
                                                                                                type="text"
                                                                                                value={formData.last_name}
                                                                                                onChange={(e) => handleInputChange('last_name', e.target.value)}
                                                                                                className="input input-bordered"
                                                                                                placeholder="Enter last name"
                                                                                        />
                                                                                </div>
                                                                        </div>
                                                                        
                                                                        {/* Password Fields (only for create mode) */}
                                                                        {modal.mode === 'create' && (
                                                                                <div className="grid grid-cols-2 gap-4">
                                                                                        <div className="form-control">
                                                                                                <label className="label">
                                                                                                        <span className="label-text font-semibold">Password *</span>
                                                                                                </label>
                                                                                                <input
                                                                                                        type="password"
                                                                                                        value={formData.password}
                                                                                                        onChange={(e) => handleInputChange('password', e.target.value)}
                                                                                                        className="input input-bordered"
                                                                                                        placeholder="Enter password"
                                                                                                />
                                                                                        </div>
                                                                                        
                                                                                        <div className="form-control">
                                                                                                <label className="label">
                                                                                                        <span className="label-text font-semibold">Confirm Password *</span>
                                                                                                </label>
                                                                                                <input
                                                                                                        type="password"
                                                                                                        value={formData.confirm_password}
                                                                                                        onChange={(e) => handleInputChange('confirm_password', e.target.value)}
                                                                                                        className="input input-bordered"
                                                                                                        placeholder="Confirm password"
                                                                                                />
                                                                                        </div>
                                                                                </div>
                                                                        )}
                                                                        
                                                                        {/* Status */}
                                                                        <div className="form-control">
                                                                                <label className="label cursor-pointer justify-start gap-4">
                                                                                        <input
                                                                                                type="checkbox"
                                                                                                checked={formData.is_active}
                                                                                                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                                                                                className="checkbox checkbox-primary"
                                                                                        />
                                                                                        <span className="label-text font-semibold">Active User</span>
                                                                                </label>
                                                                        </div>
                                                                        
                                                                        {/* Groups */}
                                                                        <div className="form-control">
                                                                                <label className="label">
                                                                                        <span className="label-text font-semibold">Groups</span>
                                                                                </label>
                                                                                <div className="border border-base-300 rounded-lg p-4 space-y-2 max-h-40 overflow-y-auto">
                                                                                        {availableGroups.length > 0 ? (
                                                                                                availableGroups.map(group => (
                                                                                                        <label key={group.id} className="label cursor-pointer justify-start gap-4">
                                                                                                                <input
                                                                                                                        type="checkbox"
                                                                                                                        checked={formData.groups.includes(group.id)}
                                                                                                                        onChange={(e) => {
                                                                                                                                if (e.target.checked) {
                                                                                                                                        handleInputChange('groups', [...formData.groups, group.id]);
                                                                                                                                } else {
                                                                                                                                        handleInputChange('groups', formData.groups.filter(id => id !== group.id));
                                                                                                                                }
                                                                                                                        }}
                                                                                                                        className="checkbox checkbox-primary"
                                                                                                                />
                                                                                                                <span className="label-text">{group.name}</span>
                                                                                                        </label>
                                                                                                ))
                                                                                        ) : (
                                                                                                <p className="text-base-content/60 text-sm">No groups available</p>
                                                                                        )}
                                                                                </div>
                                                                        </div>
                                                                </div>
                                                        )}
                                                        
                                                        {modal.activeTab === 'email' && modal.user && (
                                                                <UserEmailSettings userId={modal.user.id} />
                                                        )}
                                                </div>
                                                
                                                {/* Modal Footer - Only for details tab */}
                                                {modal.activeTab === 'details' && (
                                                        <div className="sticky bottom-0 bg-base-100 px-6 py-4 border-t border-base-300">
                                                                <div className="flex justify-end gap-2">
                                                                        <button onClick={closeUserModal} className="btn btn-ghost">
                                                                                Cancel
                                                                        </button>
                                                                        <button 
                                                                                onClick={handleSaveUser} 
                                                                                className="btn btn-primary"
                                                                                disabled={saving}
                                                                        >
                                                                                {saving ? (
                                                                                        <>
                                                                                                <FaSpinner className="animate-spin mr-2" />
                                                                                                Saving...
                                                                                        </>
                                                                                ) : (
                                                                                        modal.mode === 'edit' ? 'Save Changes' : 'Create User'
                                                                                )}
                                                                        </button>
                                                                </div>
                                                        </div>
                                                )}
                                        </div>
                                </div>
                        )}
                </div>
        );
};

export default Users;